import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'

import { Row } from '@tanstack/react-table'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import { isAxiosError } from '@/api/business'
import { ProductTable } from '../..'
import { updateProduct } from '@/api/business/product'
import { ProductsKeys } from '@/queries/useProductQuery'
import Button from '@/components/Button'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form' 
import { Input } from '@/components/ui/input'
import { useEffect } from 'react' 
import { Checkbox } from '@/components/ui/checkbox'

interface EditDialogProps {
    row: Row<ProductTable>
    isOpen: boolean
    onClose: () => void
}

const EditDialog = ({ row, isOpen, onClose }: EditDialogProps) => {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    const schema = z.object({
       DS_PRODUTO: z.string().min(1, {
            message: 'O campo produto é obrigatório',
        }).max(100, 'Quantidade máxima de 100 caracteres'),
        DD_SKU: z.string().min(1, {
            message: 'O campo embalagem é obrigatório',
        }).max(45, 'Quantidade máxima de 45 caracteres'),
        DD_UNIDADE_VENDA: z.string().min(1, {
            message: 'O campo unidade de venda é obrigatório',
        }),
        DD_MARCA: z.string().min(1, {
            message: 'O campo marca é obrigatório',
        }).max(50, 'Quantidade máxima de 50 caracteres'),
        DS_DETENTOR: z.string().min(1, {
            message: 'O campo fabricante é obrigatório',
        }).max(100, 'Quantidade máxima de 100 caracteres'),
        DD_PRODUTO_VALIDADO: z.number()
    })

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: { 
            DS_PRODUTO: row.original.DS_PRODUTO || "", 
            DD_SKU: row.original.DD_SKU || "", 
            DD_UNIDADE_VENDA: row.original.DD_UNIDADE_VENDA.toString() || "", 
            DD_MARCA: row.original.DD_MARCA || "", 
            DS_DETENTOR: row.original.DS_DETENTOR || "",
            DD_PRODUTO_VALIDADO: row.original.DD_PRODUTO_VALIDADO || 0
        },
    })

    useEffect(() => {
        form.reset({
            DS_PRODUTO: row.original.DS_PRODUTO || "", 
            DD_SKU: row.original.DD_SKU || "", 
            DD_UNIDADE_VENDA: row.original.DD_UNIDADE_VENDA.toString() || "", 
            DD_MARCA: row.original.DD_MARCA || "", 
            DS_DETENTOR: row.original.DS_DETENTOR || "",
            DD_PRODUTO_VALIDADO: row.original.DD_PRODUTO_VALIDADO || 0
        })
    }, [row.original])

    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: updateProduct,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ProductsKeys.lists() })
            toast({
                title: 'Produto atualizado com sucesso',
            })
            onClose()
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível editar o produto. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao editar Produto',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSubmit = (data: z.infer<typeof schema>) => {
        mutate({
            SK_PRODUTO: row.original.SK_PRODUTO,
           ...data,
           DD_UNIDADE_VENDA: Number(data.DD_UNIDADE_VENDA)
        })
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[700px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Editar Produto</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                       <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >
                        <div className='grid grid-cols-[1fr] gap-4'>
                            <FormField
                                name="DS_PRODUTO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Produto</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                        <div className='grid grid-cols-[1fr,1fr,1fr] gap-4'>
                            <FormField
                                name="DD_SKU"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Embalagem</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />  
                            <FormField
                                name="DD_UNIDADE_VENDA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Unidade venda</FormLabel>
                                        <FormControl>
                                            <Input {...field} type='number'/>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="DD_MARCA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Marca</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                         <div className='grid grid-cols-[1fr] gap-4'> 
                            <FormField
                                name="DS_DETENTOR"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Fabricante</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                control={form.control}
                                name="DD_PRODUTO_VALIDADO"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-end space-x-3 pb-0.5">
                                        <FormControl>
                                            <Checkbox
                                                checked={(field.value == 1 ? true : false)}
                                                onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                                            />
                                        </FormControl>
                                        <div className=" leading-none">
                                            <FormLabel>
                                                Produto apto para utilizar?
                                            </FormLabel> 
                                            <FormMessage />
                                        </div>
                                    </FormItem>
                                )}
                            />
                        </div> 
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default EditDialog
